import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { Download } from "lucide-react";

const STORAGE_KEY = "nuitcalme_leads";

const readLeads = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
};

const saveLead = (lead) => {
  const leads = readLeads();
  leads.push(lead);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(leads));
  return leads;
};

const exportCSV = (leads) => {
  const header = "prenom,email,date\n";
  const rows = leads
    .map(
      (l) =>
        `"${(l.prenom || "").replace(/"/g, '""')}","${(l.email || "").replace(
          /"/g,
          '""'
        )}","${l.date}"`
    )
    .join("\n");
  const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `nuit-calme-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const easeOutCubic = [0.33, 1, 0.68, 1];

/* Shared form logic + presentation. variant: "inline" | "card" */
const useEmailForm = () => {
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [savedName, setSavedName] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!prenom.trim() || !email.trim()) return;
    saveLead({
      prenom: prenom.trim(),
      email: email.trim(),
      date: new Date().toISOString(),
    });
    setSavedName(prenom.trim());
    setDone(true);
    setPrenom("");
    setEmail("");
    window.dispatchEvent(new Event("nuitcalme-lead"));
  };

  return { prenom, setPrenom, email, setEmail, done, savedName, submit };
};

const Confirmation = ({ name, className = "" }) => (
  <motion.p
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, ease: easeOutCubic }}
    data-testid="email-confirmation"
    className={`text-base leading-relaxed text-[#e8c97a] ${className}`}
  >
    🌙 C’est noté, {name}. Vérifie ta boîte, quelque chose arrive ce soir.
  </motion.p>
);

export const HeroEmailForm = () => {
  const f = useEmailForm();

  return (
    <div className="mx-auto mt-8 w-full max-w-md">
      <AnimatePresence mode="wait">
        {f.done ? (
          <Confirmation key="c" name={f.savedName} className="text-center" />
        ) : (
          <motion.form
            key="f"
            onSubmit={f.submit}
            data-testid="hero-email-form"
            className="flex flex-col gap-2.5 sm:flex-row"
          >
            <input
              data-testid="hero-email-prenom"
              value={f.prenom}
              onChange={(e) => f.setPrenom(e.target.value)}
              required
              placeholder="Prénom"
              className="w-full rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm text-white placeholder-white/40 outline-none transition-colors duration-300 focus:border-[#c9a84c]/60 sm:w-32"
            />
            <input
              data-testid="hero-email-input"
              type="email"
              value={f.email}
              onChange={(e) => f.setEmail(e.target.value)}
              required
              placeholder="Ton email"
              className="w-full flex-1 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm text-white placeholder-white/40 outline-none transition-colors duration-300 focus:border-[#c9a84c]/60"
            />
            <button
              data-testid="hero-email-submit"
              type="submit"
              className="whitespace-nowrap rounded-full bg-[#c9a84c] px-6 py-3 text-sm font-semibold text-[#0a0e1a] transition-colors duration-300 hover:bg-[#e8c97a] active:scale-[0.98]"
            >
              Je veux mieux dormir →
            </button>
          </motion.form>
        )}
      </AnimatePresence>
      {!f.done && (
        <p className="mt-3 text-center text-xs text-white/40">
          Gratuit. Sans spam. Tu te désabonnes quand tu veux.
        </p>
      )}
    </div>
  );
};

export const EmailCaptureSection = () => {
  const f = useEmailForm();
  const [count, setCount] = useState(0);

  useEffect(() => {
    const update = () => setCount(readLeads().length);
    update();
    window.addEventListener("nuitcalme-lead", update);
    return () => window.removeEventListener("nuitcalme-lead", update);
  }, []);

  return (
    <section
      id="newsletter"
      data-testid="email-capture-section"
      className="relative overflow-hidden scroll-mt-24 px-6 py-24 md:py-32 lg:px-10"
    >
      {/* Subtle stars */}
      <div className="starfield opacity-70" aria-hidden="true">
        <div className="star-layer star-layer--sm" />
        <div className="star-layer star-layer--md star-twinkle" />
      </div>
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[380px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#2a1f4a] opacity-40 blur-[130px] breathe"
        aria-hidden="true"
      />

      <Reveal className="relative z-10 mx-auto max-w-xl">
        <div className="card-line rounded-3xl border border-white/10 bg-[#211640] p-8 text-center sm:p-12">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a84c]">
            Newsletter du soir
          </p>
          <h2 className="text-3xl font-light leading-tight tracking-tight sm:text-4xl">
            Commence ce soir,
            <span className="text-gold-gradient italic"> gratuitement.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-white/60">
            Reçois 3 conseils pour mieux dormir ce soir. Gratuit. Sans spam. Tu te
            désabonnes quand tu veux.
          </p>

          <div className="mt-9">
            <AnimatePresence mode="wait">
              {f.done ? (
                <Confirmation key="c" name={f.savedName} />
              ) : (
                <motion.form
                  key="f"
                  onSubmit={f.submit}
                  data-testid="section-email-form"
                  className="flex flex-col gap-3"
                >
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                      data-testid="section-email-prenom"
                      value={f.prenom}
                      onChange={(e) => f.setPrenom(e.target.value)}
                      required
                      placeholder="Prénom"
                      className="w-full rounded-full border border-white/15 bg-[#0a0e1a]/60 px-5 py-3.5 text-sm text-white placeholder-white/40 outline-none transition-colors duration-300 focus:border-[#c9a84c]/60 sm:w-40"
                    />
                    <input
                      data-testid="section-email-input"
                      type="email"
                      value={f.email}
                      onChange={(e) => f.setEmail(e.target.value)}
                      required
                      placeholder="Ton email"
                      className="w-full flex-1 rounded-full border border-white/15 bg-[#0a0e1a]/60 px-5 py-3.5 text-sm text-white placeholder-white/40 outline-none transition-colors duration-300 focus:border-[#c9a84c]/60"
                    />
                  </div>
                  <button
                    data-testid="section-email-submit"
                    type="submit"
                    className="w-full rounded-full bg-[#c9a84c] py-3.5 text-sm font-semibold text-[#0a0e1a] transition-colors duration-300 hover:bg-[#e8c97a] active:scale-[0.98]"
                  >
                    Je veux mieux dormir →
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {count > 0 && (
            <button
              data-testid="export-contacts-csv"
              onClick={() => exportCSV(readLeads())}
              className="mx-auto mt-7 inline-flex items-center gap-2 text-xs text-white/40 transition-colors duration-300 hover:text-[#e8c97a]"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.6} />
              Exporter les contacts (CSV) · {count}
            </button>
          )}
        </div>
      </Reveal>
    </section>
  );
};
